import { z } from 'zod';
/**
 * What a film is called, and in what order you would rather read it.
 *
 * Every member carries an ordered list of the three languages a title can be
 * in. The first one the film actually *has* is the title they see, everywhere
 * on the site; the next distinct one is the small line under it. That is the
 * whole feature, and it lives here rather than in a component because six
 * surfaces render a title and they must not each invent their own fallback.
 *
 * ## Why the chain needs honest nulls
 *
 * TMDB answers `language=sr-RS` for a film with no Serbian translation by
 * handing back the English title in the `title` field — it does not say "no".
 * Before this existed, that string was written to `title_sr` and the site
 * showed it as the Serbian title, which was a lie nobody could see. It is also
 * exactly what would break a preference list: a chain can only fall through a
 * slot that is *empty*, and a slot filled with someone else's title is never
 * empty. So `lib/tmdb.ts` now stores `null` there, and every title on the wire
 * is nullable. `movieTitle()` is what puts a string back on the screen.
 *
 * The final fallback is 'Bez naslova' rather than an empty string: a card with
 * a blank where its name goes reads as a bug, and a film with no title at all
 * in any of the three is a row somebody should go and fix.
 */
export const TITLE_LANGS = ['sr', 'en', 'orig'];
/** Serbian first — decision #1's spirit, and what everyone gets on day one. */
export const DEFAULT_TITLE_ORDER = ['sr', 'en', 'orig'];
/** On the slats of the sign at the bottom of the page. */
export const TITLE_LANG_NAMES = {
    sr: 'Srpski',
    en: 'Engleski',
    orig: 'Original',
};
export const NO_TITLE = 'Bez naslova';
export const titleLangSchema = z.enum(TITLE_LANGS);
/**
 * A permutation of the three, never a subset.
 *
 * "Serbian, then original" with English left out is a tempting shape and it is
 * the wrong one: it has no answer for a film that has only an English title,
 * and the honest answer to "what do I show then" is "the one thing we have".
 * So the list is always all three, and last place is where you put a language
 * you never want to see unless it is the only one left.
 */
export const titleOrderSchema = z
    .array(titleLangSchema)
    .length(TITLE_LANGS.length)
    .refine((order) => new Set(order).size === TITLE_LANGS.length, {
    message: 'Redosled mora da sadrži sva tri jezika, svaki po jednom.',
});
/**
 * Repair anything that is not a clean permutation, instead of throwing.
 *
 * The wire is validated by the schema above, so this is for the other
 * direction: a `text[]` read back out of Postgres, where a column added in a
 * migration could be short, and where a language added to `TITLE_LANGS` later
 * would leave every existing row missing one. Keeping what is there and
 * appending what is missing means a new language lands *last* for everybody,
 * which is the only default that cannot silently change what people see.
 */
export function normalizeTitleOrder(input) {
    const known = new Set(TITLE_LANGS);
    const seen = new Set();
    const out = [];
    if (Array.isArray(input)) {
        for (const entry of input) {
            if (typeof entry !== 'string' || !known.has(entry))
                continue;
            const lang = entry;
            if (seen.has(lang))
                continue;
            seen.add(lang);
            out.push(lang);
        }
    }
    for (const lang of TITLE_LANGS)
        if (!seen.has(lang))
            out.push(lang);
    return out;
}
/**
 * The same three fields as a schema, for wire shapes that carry a film's names
 * without carrying the film — the pitch winner on a board card is the one that
 * exists today. `movieSchema` declares them itself rather than extending this,
 * because a movie is not a title set with extras: it is a row that happens to
 * have three names.
 */
export const titleSetSchema = z.object({
    titleSr: z.string().nullable(),
    titleEn: z.string().nullable(),
    titleOrig: z.string().nullable(),
});
export function titleByLang(titles, lang) {
    const raw = lang === 'sr' ? titles.titleSr : lang === 'en' ? titles.titleEn : titles.titleOrig;
    const text = raw?.trim();
    return text ? text : null;
}
/**
 * Walk the list, take the first two distinct titles.
 *
 * "Distinct" is case-insensitive, and that is not fussiness: TMDB returns the
 * English title in the original-title field for most English-language films,
 * so the second line would otherwise repeat the first on half the catalogue.
 * It is the same rule the importer used to apply by writing `title_orig = null`
 * — moved here, where it can answer the question for any pair of languages
 * rather than only for that one.
 */
export function resolveTitle(titles, order = DEFAULT_TITLE_ORDER) {
    const empty = {
        primary: NO_TITLE,
        secondary: null,
        primaryLang: null,
        secondaryLang: null,
    };
    if (!titles)
        return empty;
    const langs = normalizeTitleOrder(order);
    let primary = null;
    let primaryLang = null;
    for (const lang of langs) {
        const text = titleByLang(titles, lang);
        if (text === null)
            continue;
        if (primary === null) {
            primary = text;
            primaryLang = lang;
            continue;
        }
        if (text.toLocaleLowerCase() === primary.toLocaleLowerCase())
            continue;
        return { primary, secondary: text, primaryLang, secondaryLang: lang };
    }
    if (primary === null)
        return empty;
    return { primary, secondary: null, primaryLang, secondaryLang: null };
}
/** The one-line form, for the places that have room for exactly one title. */
export function movieTitle(titles, order = DEFAULT_TITLE_ORDER) {
    return resolveTitle(titles, order).primary;
}
/**
 * Move one language to a new position, keeping the rest in their order.
 *
 * Shared with the API only so a test can reach it; the sign at the bottom of
 * the page is the one caller. It is here rather than in the component because
 * "the order after a drag" is the kind of arithmetic that is wrong in a way
 * nobody notices until two languages swap places on a phone.
 */
export function moveTitleLang(order, lang, toIndex) {
    const current = normalizeTitleOrder(order);
    const from = current.indexOf(lang);
    if (from === -1)
        return current;
    const target = Math.max(0, Math.min(current.length - 1, toIndex));
    if (from === target)
        return current;
    const next = [...current];
    next.splice(from, 1);
    next.splice(target, 0, lang);
    return next;
}
//# sourceMappingURL=titles.js.map